import { createApp, ref, onMounted, onUnmounted, watch, Transition } from './vue.esm-browser.prod.js';
import Dropdown from './Dropdown.js';
import DropdownOption from './DropdownOption.js';
import Chevron from './Chevron.js';
import VariantPills from './VariantPills.js';
import { html } from './helper-functions.js';
import { addToCart } from './add-to-cart-functions.js';
import {
  createPurchaseTypeOptions,
  createQuantityOptions,
  createFrequencyOptions,
  createVariantOptions,
} from './product-purchase-functions.js';

// Create shared state that persists across multiple component instances
let sharedSetupResult = null;

const rootComponent = {
  setup() {
    // If we already have a setup result, reuse it for state sharing
    if (sharedSetupResult) {
      return sharedSetupResult;
    }
    /** @typedef {{ dropdownId: string, value: string, text: string[] }} Option */

    const purchaseTypeOptions = ref([]);
    const quantityOptions = ref([]);
    const frequencyOptions = ref([]);
    const variantOptions = ref([]);
    const activePurchaseType = ref({ dropdownId: 'purchaseTypeOptions', value: 'onetime', text: [''] });
    const selectedQuantity = ref({ dropdownId: 'quantityOptions', value: 1, text: [''] });
    const selectedFrequency = ref({ dropdownId: 'frequencyOptions', sellingPlanId: NaN, value: 1, text: [''] });
    const selectedVariantId = ref();
    const selectedVariantOption = ref({ dropdownId: 'variantOptions', value: NaN, text: [''] });
    const subscriptionPct = ref(0);
    const productObj = ref();
    const allProducts = ref([]);
    const addToCartButtons = ref([]);


    onMounted(async () => {
      // Attach event listeners to ALL buy buttons (mobile and desktop)
      const allButtons = document.querySelectorAll('.sticky-add-to-cart__column--button add-to-cart-component');
      addToCartButtons.value = Array.from(allButtons);
      addToCartButtons.value.forEach(button => {
        button.addEventListener('click', handleAddToCartButtonClick);
      });

      loadProductData();
    });


    onUnmounted(() => {
      addToCartButtons.value.forEach(button => {
        button.removeEventListener('click', handleAddToCartButtonClick);
      });
    })


    function loadProductData() {
      const script = document.querySelector('#all-products-data');
      const jsonText = script?.textContent;
      if (!jsonText) return;
      allProducts.value = JSON.parse(jsonText);

      const url = window.location.href;
      const urlObj = new URL(url);
      const { pathname } = urlObj;
      const productHandle = pathname.split('/').pop();
      productObj.value = allProducts.value.find((/** @type {any} */p) => p.handle === productHandle);
      if (!productObj.value) return;  

      subscriptionPct.value = productObj.value.selling_plan_groups?.[0].selling_plans?.[0]?.price_adjustments?.[0].value;

      setVariantOptions(productObj.value);
      selectedVariantId.value = NaN;
      if (productObj.value.variants?.length > 1) {
        /** @type {HTMLSelectElement|null} */
        const selectEl = document.querySelector('select.variant-option__select');
        const foundVariant = productObj.value?.variants
          .find((/** @type {any} */ variant) => variant.title === selectEl?.value);
        selectedVariantId.value = foundVariant?.id;
        selectedVariantOption.value = variantOptions.value.find((/** @type {any} */ v) => v.value === selectedVariantId.value)
      } else {
        selectedVariantId.value = productObj.value?.variants?.[0]?.id;
        selectedVariantOption.value = variantOptions.value[0];
      }
      updateVariantPrice();

      setPurchaseTypeOptions(productObj.value, subscriptionPct.value);
      setQuantityOptions(productObj.value, subscriptionPct.value);
      setFrequencyOptions(productObj.value);
    }


    function handleAddToCartButtonClick(/** @type {PointerEvent} */ e) {
      if (activePurchaseType.value.value === 'onetime') {
        addToCart(selectedVariantId.value, 1, null);
        return;
      }

      const quantity = parseInt(selectedQuantity.value.value, 10);
      const sellingPlanId = selectedFrequency.value?.sellingPlanId;
      addToCart(selectedVariantId.value, quantity, sellingPlanId);
    }


    /** @param {Record<string, any>} productObj @param {number} subscriptionPct */
    function setPurchaseTypeOptions(productObj, subscriptionPct) {
      purchaseTypeOptions.value = createPurchaseTypeOptions(productObj, subscriptionPct);
      activePurchaseType.value = purchaseTypeOptions.value[selectedVariantId.value][1];
    }


    /** @param {Record<string, any>} productObj @param {number} subscriptionPct */
    function setQuantityOptions(productObj, subscriptionPct) {
      quantityOptions.value = createQuantityOptions(productObj, subscriptionPct);

      // Find the variant to check its price
      const variant = productObj.variants.find((/** @type {any} */ v) => v.id === selectedVariantId.value);

      // Default to quantity 2 (index 1) if price < £20 (2000 pence), otherwise quantity 1 (index 0)
      const defaultIndex = variant && variant.price < 2000 ? 1 : 0;
      selectedQuantity.value = quantityOptions.value[selectedVariantId.value][defaultIndex];
    }


    /** @param {Record<string, any>} productObj */
    function setFrequencyOptions(productObj) {
      frequencyOptions.value = createFrequencyOptions(productObj);
      selectedFrequency.value = frequencyOptions.value[0];
    }


    /** @param {Record<string, any>} productObj */
    function setVariantOptions(productObj) {
      variantOptions.value = createVariantOptions(productObj);
    }


    /** @param {Record<'dropdownId' | 'value' | 'text', number|string>} option */
    function handleSelectOptionClick(option) {
      switch(option.dropdownId) {
        case 'purchaseTypeOptions': {
          activePurchaseType.value = option;
          break;
        }

        case 'quantityOptions': {
          selectedQuantity.value = option;
          // @ts-ignore
          window.selectedQuantity = option;
          break;
        }

        case 'frequencyOptions': {
          selectedFrequency.value = option;
          break;
        }

        case 'variantOptions': {
          selectedVariantOption.value = option;
          selectedVariantId.value = selectedVariantOption.value.value;
          updateVariantPrice();
          break;
        }
      }
    }


    function updateVariantPrice() {
      const foundVariant = productObj.value.variants
        .find((/** @type {any} */ v) => v.id === selectedVariantId.value);
      const variantPrice = `£${(foundVariant.price / 100).toFixed(2)}`;
      const priceUnderTitleEl = document.querySelector('#StickyAddToCart .sticky-add-to-cart__price');
      if (!priceUnderTitleEl) return;
      priceUnderTitleEl.textContent = variantPrice;
    }


    watch(selectedFrequency, (/** @type {Option} */ newVal) => {
      const originalSelectId = `selling_plan_${productObj.value.id}`;
      /** @type {HTMLSelectElement | null} */
      const originalSelectEl = document.querySelector(`#${originalSelectId}`);
      if (!originalSelectEl) return;
      
      // @ts-ignore
      originalSelectEl.value = newVal?.sellingPlanId;
    });


    watch(selectedVariantOption, () => {
      setPurchaseTypeOptions(productObj.value, subscriptionPct.value);
      setQuantityOptions(productObj.value, subscriptionPct.value);
    });

    sharedSetupResult = {
      activePurchaseType,
      selectedQuantity,
      selectedFrequency,
      selectedVariantOption,
      selectedVariantId,
      purchaseTypeOptions,
      quantityOptions,
      frequencyOptions,
      variantOptions,
      handleSelectOptionClick,
      handleAddToCartButtonClick,
    };

    return sharedSetupResult;
  },

  template: html`
    <div class="FloatingCTA">
      <div v-if="variantOptions?.length > 1" class="FloatingCTA__variant-options-container">
        <VariantPills
          data-dropdown-type="variant"
          :options="variantOptions"
          :selectedOption="selectedVariantOption"
          @select-option-click="handleSelectOptionClick"
        />
      </div>

      <div class="FloatingCTA__purchase-types-outer-container">
        <div class="FloatingCTA__purchase-types-container">
          <Dropdown
            :options="purchaseTypeOptions[selectedVariantId]"
            :selectedOption="activePurchaseType"
            @select-option-click="handleSelectOptionClick"
          />
        </div>

        <Transition name="slide-fade">
          <div v-if="activePurchaseType.value === 'subscription'" class="FloatingCTA__purchase-type-options">
            <Dropdown
              :options="quantityOptions[selectedVariantId]"
              :selectedOption="selectedQuantity"
              @select-option-click="handleSelectOptionClick"
            />
            <Dropdown
              :options="frequencyOptions"
              :selectedOption="selectedFrequency"
              @select-option-click="handleSelectOptionClick"
            />
          </div>
        </Transition>
      </div>
    </div>
  `,
}

// Components for split layout
const variantsOnly = {
  setup: rootComponent.setup,
  template: html`
    <div class="FloatingCTA">
      <div v-if="variantOptions?.length > 1" class="FloatingCTA__variant-options-container">
        <VariantPills
          data-dropdown-type="variant"
          :options="variantOptions"
          :selectedOption="selectedVariantOption"
          @select-option-click="handleSelectOptionClick"
        />
      </div>
    </div>
  `,
};

const dropdownsOnly = {
  setup: rootComponent.setup,
  template: html`
    <div class="FloatingCTA">
      <div class="FloatingCTA__purchase-types-outer-container">
        <div class="FloatingCTA__purchase-types-container">
          <Dropdown
            :options="purchaseTypeOptions[selectedVariantId]"
            :selectedOption="activePurchaseType"
            @select-option-click="handleSelectOptionClick"
          />
        </div>

        <Transition name="slide-fade">
          <div v-if="activePurchaseType.value === 'subscription'" class="FloatingCTA__purchase-type-options">
            <Dropdown
              :options="quantityOptions[selectedVariantId]"
              :selectedOption="selectedQuantity"
              @select-option-click="handleSelectOptionClick"
            />
            <Dropdown
              :options="frequencyOptions"
              :selectedOption="selectedFrequency"
              @select-option-click="handleSelectOptionClick"
            />
          </div>
        </Transition>
      </div>
    </div>
  `,
};

// Initialize immediately when products data is ready
function initFloatingCTA() {
  const variantsTarget = document.querySelector('#StickyAddToCart__variants-container');
  const dropdownsTarget = document.querySelector('#StickyAddToCart__dropdowns-container');
  const parentContainer = document.querySelector('.StickyAddToCart__dropdowns-buy-button-container');

  if (variantsTarget && dropdownsTarget && parentContainer) {
    // Mobile split layout - mount variants and dropdowns separately
    const variantsApp = createApp(variantsOnly);
    variantsApp.component('VariantPills', VariantPills);
    variantsApp.component('Transition', Transition);
    variantsApp.mount('#StickyAddToCart__variants-container');

    const dropdownsApp = createApp(dropdownsOnly);
    dropdownsApp.component('Dropdown', Dropdown);
    dropdownsApp.component('DropdownOption', DropdownOption);
    dropdownsApp.component('Chevron', Chevron);
    dropdownsApp.component('Transition', Transition);
    dropdownsApp.mount('#StickyAddToCart__dropdowns-container');

    // Also mount full component for desktop (will be hidden on mobile via CSS)
    // Insert before the bottom row so the button stays after the desktop component
    const desktopWrapper = document.createElement('div');
    desktopWrapper.id = 'desktop-floating-cta';
    const bottomRow = document.querySelector('.StickyAddToCart__bottom-row');
    parentContainer.insertBefore(desktopWrapper, bottomRow);

    const desktopApp = createApp(rootComponent);
    desktopApp.component('Dropdown', Dropdown);
    desktopApp.component('DropdownOption', DropdownOption);
    desktopApp.component('Chevron', Chevron);
    desktopApp.component('VariantPills', VariantPills);
    desktopApp.component('Transition', Transition);
    desktopApp.mount('#desktop-floating-cta');
  } else {
    console.error('FloatingCTA mount targets not found');
  }
}

// Wait for products data to be ready
document.addEventListener('products-data-loaded', initFloatingCTA);

// Try immediately if data is already loaded
if (document.querySelector('#all-products-data')?.textContent) {
  initFloatingCTA();
}